// actions
import * as Actions from '../actions';
import { AppState } from '../reducers';

// services
import * as Services from '../../services/PractitionerAccountStatement.Services';
import { GetDebtsBySettlementId } from '../../services/CashFlow-Debts.Services';

// models
import { Debt } from '../../models/Entities/CashFlow/Debt/Debt';
import { ErrorType } from '../../models/HttpError';
import { StatementSection } from '../../models/Entities/PractitionerAccountStatement/PractitionerAccountStatement';

type KnownAction = Actions.PractitionerAccountStatement.PractitionerAccountStatementKnownAction;

/** Thunk que además devuelve una promesa: la pantalla sabe cuándo terminó y precarga el pago con lo que trae. */
export type PractitionerStatementThunk<TResult> = (
    dispatch: (action: KnownAction) => void,
    getState: () => AppState
) => Promise<TResult>;

export interface StatementPeriod {
    from?: Date;
    to?: Date;
}

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const toErrorType = (error: unknown): ErrorType => {
    if (typeof error === 'object' && error !== null && 'ErrorCode' in error) return error as ErrorType;
    return { ErrorCode: 0, ErrorMessage: error instanceof Error ? error.message : 'Unexpected error', Errors: [] };
};

const tokenOf = (getState: () => AppState): string | undefined => getState().oidc?.user?.access_token;

/**
 * Carga un bloque y despacha su resultado. Nunca rechaza: el error queda en el
 * bloque, que muestra su banner con "Reintentar".
 */
const loadSection = async <T>(
    dispatch: (action: KnownAction) => void,
    practitionerId: string,
    section: StatementSection,
    load: () => Promise<{ data: T | undefined; source?: 'endpoint' | 'fallback' }>
): Promise<T | undefined> => {
    dispatch({ type: 'PRACTITIONER_STATEMENT_SECTION_REQUEST_ACTION', practitionerId, section });
    try {
        const result = await load();
        if (result.data === undefined) {
            dispatch({ type: 'PRACTITIONER_STATEMENT_SECTION_UNAVAILABLE_ACTION', practitionerId, section });
            return undefined;
        }
        dispatch({
            type: 'PRACTITIONER_STATEMENT_SECTION_RECEIVE_ACTION',
            practitionerId,
            section,
            data: result.data as any,
            source: result.source,
        });
        return result.data;
    } catch (error: unknown) {
        dispatch({ type: 'PRACTITIONER_STATEMENT_SECTION_FAIL_ACTION', practitionerId, section, error: toErrorType(error) });
        return undefined;
    }
};

const failAll = (dispatch: (action: KnownAction) => void, practitionerId: string, sections: StatementSection[]) =>
    sections.forEach((section) =>
        dispatch({ type: 'PRACTITIONER_STATEMENT_SECTION_FAIL_ACTION', practitionerId, section, error: NotAuthorized })
    );

const MONEY_SECTIONS: StatementSection[] = ['debts', 'settlementSummary', 'payments', 'settlements'];

export const actionCreators = {
    /**
     * Lo que se le debe al profesional: deudas, resumen por liquidación, pagos y
     * liquidaciones. `debtorId` es el centro (tenant). Con 404 en los endpoints
     * nuevos usa los reemplazos (el bloque queda con `source: 'fallback'`).
     */
    LoadMoneySections: (practitionerId: string, debtorId: string): PractitionerStatementThunk<void> => {
        return async (dispatch, getState) => {
            const token = tokenOf(getState);
            if (!token) {
                failAll(dispatch, practitionerId, MONEY_SECTIONS);
                return;
            }

            // Las deudas se piden una sola vez: las usa la tabla de pendientes y, si
            // hace falta, el resumen por liquidación armado en el cliente.
            let debtsRequest: Promise<Debt[]> | undefined;
            const debts = () => {
                debtsRequest = debtsRequest ?? Services.GetPractitionerDebts(token, practitionerId, debtorId);
                return debtsRequest;
            };

            await Promise.all([
                loadSection(dispatch, practitionerId, 'debts', async () => ({ data: await debts() })),
                loadSection(dispatch, practitionerId, 'settlementSummary', async () => {
                    const result = await Services.GetSettlementSummaryWithFallback(token, practitionerId, debtorId, debts);
                    return { data: result.items, source: result.source };
                }),
                loadSection(dispatch, practitionerId, 'payments', async () => {
                    const result = await Services.GetCreditorPaymentsWithFallback(token, practitionerId, debtorId);
                    return { data: result.items, source: result.source };
                }),
                loadSection(dispatch, practitionerId, 'settlements', async () => ({
                    data: await Services.GetPractitionerSettlements(token, practitionerId),
                })),
            ]);
        };
    },

    /** Pacientes a cargo (MedicalProtocols §11.2). 404 → "dato no disponible todavía". */
    LoadPatientsSummary: (practitionerId: string): PractitionerStatementThunk<void> => {
        return async (dispatch, getState) => {
            const token = tokenOf(getState);
            if (!token) {
                failAll(dispatch, practitionerId, ['patients']);
                return;
            }
            await loadSection(dispatch, practitionerId, 'patients', async () => ({
                data: await Services.GetPractitionerPatientsSummary(token, practitionerId),
            }));
        };
    },

    /** Visitas del período (MedicalProtocols §11.2). 404 → "dato no disponible todavía". */
    LoadVisitsSummary: (practitionerId: string, period: StatementPeriod = {}): PractitionerStatementThunk<void> => {
        return async (dispatch, getState) => {
            const token = tokenOf(getState);
            if (!token) {
                failAll(dispatch, practitionerId, ['visits']);
                return;
            }
            await loadSection(dispatch, practitionerId, 'visits', async () => ({
                data: await Services.GetPractitionerVisitsSummary(token, practitionerId, period.from, period.to),
            }));
        };
    },

    /**
     * Deudas pendientes del profesional en esas liquidaciones, para precargar el
     * pago. No toca el store de la caja general. Rechaza con ErrorType.
     */
    GetSettlementDebtsForPayment: (practitionerId: string, settlementIds: number[]): PractitionerStatementThunk<Debt[]> => {
        return async (dispatch, getState) => {
            const token = tokenOf(getState);
            if (!token) {
                dispatch({ type: 'PRACTITIONER_STATEMENT_SETTLEMENT_DEBTS_FAIL_ACTION', practitionerId, settlementIds, error: NotAuthorized });
                throw NotAuthorized;
            }
            dispatch({ type: 'PRACTITIONER_STATEMENT_SETTLEMENT_DEBTS_REQUEST_ACTION', practitionerId, settlementIds });
            try {
                const lists = await Promise.all(settlementIds.map((id) => GetDebtsBySettlementId(token, id, undefined, true)));
                const id = practitionerId.toLowerCase();
                const debts = lists
                    .flat()
                    .filter((debt) => !!debt && (debt.creditor || '').toLowerCase() === id && !debt.isDebtSettled && (debt.pendingAmount ?? 0) > 0);
                dispatch({ type: 'PRACTITIONER_STATEMENT_SETTLEMENT_DEBTS_RECEIVE_ACTION', practitionerId, settlementIds, count: debts.length });
                return debts;
            } catch (error: unknown) {
                const httpError = toErrorType(error);
                dispatch({ type: 'PRACTITIONER_STATEMENT_SETTLEMENT_DEBTS_FAIL_ACTION', practitionerId, settlementIds, error: httpError });
                throw httpError;
            }
        };
    },
};
